import type { LinkNodeAttributes, LinkNodeAttributeSpec, LinkNodeOptions } from "./types";
import { isNodeSelection, mergeAttributes, Node, VueNodeViewRenderer } from "@tiptap/vue-3";
import { setHttpsProtocolIfNeeded } from "@/common/lib/string";
import { preventNodeMarks } from "../preventNodeMarks";
import { enterKeyHandler } from "./enterKeyHandler";
import { linkMarkToLinkTransformer } from "./linkMarkToLinkTransformer";
import LinkNode from "./LinkNode.vue";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    linkNode: {
      /**
       * Set link
       */
      setLinkNode: (attributes: LinkNodeAttributes) => ReturnType;

      /**
       * Unlink
       */
      unsetLinkNode: () => ReturnType;
    };
  }
}

export default Node.create<LinkNodeOptions>({
  name: "linkNode",
  marks: "",
  group: "inline",
  defining: true,
  inline: true,
  atom: true,

  addAttributes() {
    return {
      url: {
        default: null,
        parseHTML: element => element.getAttribute("href"),
        renderHTML: attrs => ({ href: (attrs as LinkNodeAttributes).url || "" }),
      },
      text: {
        default: null,
        rendered: false,
        parseHTML: element => {
          const text = element.textContent;
          const href = element.getAttribute("href");
          // when link text matches the href attribute keep text default value (null)
          if (text === href) {
            return null;
          }
          return text;
        },
      },
    } satisfies LinkNodeAttributeSpec;
  },

  parseHTML() {
    return [{
      tag: `a[data-type="${this.name}"]`,
      // set priority higher than default 'link' mark
      priority: 51,
    }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as LinkNodeAttributes;
    return [
      "a",
      mergeAttributes({
        "data-type": this.name,
        href: attrs.url,
      }, HTMLAttributes),
      attrs.text || attrs.url,
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(LinkNode);
  },

  renderText({ node }) {
    const attrs = node.attrs as LinkNodeAttributes;
    return attrs.text || attrs.url;
  },

  addProseMirrorPlugins() {
    return [
      linkMarkToLinkTransformer(),

      preventNodeMarks({
        type: this.type,
      }),

      enterKeyHandler({
        type: this.type,
        getUrl(attributes) {
          const attrs = attributes as unknown as LinkNodeAttributes;
          return attrs.url;
        },
      }),
    ];
  },

  addCommands() {
    return {
      setLinkNode: attributes => ({ commands }) => {
        // normalize url to include protocol if needed
        attributes.url = setHttpsProtocolIfNeeded(attributes.url);
        return commands.insertContent({
          type: this.name,
          attrs: attributes,
        });
      },

      unsetLinkNode: () => ({ chain, state }) => {
        const { selection } = state;

        if (!isNodeSelection(selection) || selection.node.type !== this.type) {
          return false;
        }

        const linkNode = selection.node;
        const attrs = linkNode.attrs as LinkNodeAttributes | undefined;
        if (!attrs) return false;

        const textToSet = attrs.text || attrs.url;
        const textNode = state.schema.text(textToSet);

        return chain()
          .command(({ tr }) => {
            tr.replaceSelectionWith(textNode);
            return true;
          })
          .setMeta("preventAutolink", true)
          .run();
      },
    };
  },
});
